/**
 * The PDApps a key is given to (0326) — the key access dialog's Apps view. Who holds a key is part of its access list,
 * so the access list's rule decides who sees it (`keybookAccessView`, as /exposure); an app's name and variables only
 * to someone who can read its folder. A manager of the key can take it away from an app. A key is given to an app from
 * the APP's settings (Variables and secrets), by someone who can edit the app and read the key.
 */
import { useCallback, useEffect, useState } from 'react'
import { Box, Loader2, X } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirmOptional } from '@papan/core-ui/ui'
import { stripMiniappFolderExt } from '@papan/types'
import { keybookApi } from '../client/api.js'

export function KeyAppsList({ entryId }: { entryId: string }) {
  const { t } = useTranslation('keybook')
  const toast = useToast()
  const confirm = useConfirmOptional()
  const [apps, setApps] = useState<Array<{ appFolderId: string; name: string | null; variables: string[] }> | null>(null)
  const [failed, setFailed] = useState(false)
  const [canRevoke, setCanRevoke] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await keybookApi.getEntryApps(entryId)
      setApps(r.apps); setCanRevoke(r.canRevoke); setFailed(false)
    } catch {
      // Never "no app uses this key" on a failed read — that would be a false all-clear.
      setApps([]); setFailed(true)
    }
  }, [entryId])
  useEffect(() => { void load() }, [load])

  const revoke = async (app: { appFolderId: string; name: string | null; variables: string[] }) => {
    const name = app.name ? stripMiniappFolderExt(app.name) : t('access.appsUnnamed')
    // The app's code stops getting the key at its next run — what it does with it breaks; asked first.
    const ok = confirm
      ? await confirm({ title: t('access.appsRevokeTitle', { name }), message: t('access.appsRevokeBody', { variables: app.variables.join(', ') }), confirmText: t('access.appsRevokeConfirm'), type: 'danger' })
      : window.confirm(t('access.appsRevokeTitle', { name }))
    if (!ok) return
    const appFolderId = app.appFolderId
    setBusy(appFolderId)
    try {
      await keybookApi.revokeEntryApp(entryId, appFolderId)
      await load()
    } catch (err: any) {
      toast.error(err?.message || t('access.appsRevokeFailed'))
    } finally {
      setBusy(null)
    }
  }

  if (apps === null) return <Loader2 className="w-4 h-4 animate-spin themed-text-muted" />
  if (failed) return <p className="text-xs text-red-400" data-testid="key-apps-failed">{t('access.appsLoadFailed')}</p>
  if (apps.length === 0) return <p className="text-xs themed-text-muted" data-testid="key-apps-empty">{t('access.appsNone')}</p>
  return (
    <ul className="space-y-1" data-testid="key-apps">
      {apps.map(a => (
        <li key={a.appFolderId} className="flex items-center gap-2 text-xs" data-testid="key-app-row">
          <Box className="w-3.5 h-3.5 themed-icon shrink-0" />
          <span className="themed-text truncate">{a.name ? stripMiniappFolderExt(a.name) : t('access.appsUnnamed')}</span>
          <code className="themed-text-muted truncate min-w-0 flex-1 text-[11px]">{a.variables.join(', ')}</code>
          {canRevoke && (
            <button
              type="button"
              onClick={() => revoke(a)}
              disabled={busy !== null}
              className="kbg-btn w-6 h-6 rounded-md shrink-0"
              title={t('access.appsRevoke')}
              aria-label={t('access.appsRevoke')}
              data-testid="key-app-revoke"
            >
              {busy === a.appFolderId ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}
